import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { defaultAcademicYear } from '../../constants/academicYears';
import { RootState } from '../../redux/store';
import academicYearsApi from '../../apis/academicYears';
import ToastMessage from '../../components/ToastMessage';
import { SnackbarSeverityType } from '../../enums';
import AddAcademicYearDialog from './AddAcademicYearDialog';
import { useAcademicYears } from './utils/useAcademicYears';
import { useSchoolClassesConfig } from './utils/useSchoolClassesConfig';
import { GradeRow, YearRow } from './types/AcademicYearTypes';
import { UpsertAcademicYearDatesBody } from '../../types/academicYearType';
import {
  getAcademicYearColumns,
  mapYearEntriesToGridRows
} from './utils/utils';
import { AcademicYearContainer, FormHeader } from './styled';
import AcademicYearFilterCard from './components/AcademicYearFilterCard';
import AcademicYearTableCard from './components/AcademicYearTableCard';
import EditAcademicYearDialog from './EditAcademicYearDialog';
import { useAcademicYearValidation } from './utils/useValidation';

const AcademicYears = () => {
  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );

  const [academicYear, setAcademicYear] = useState<string>(defaultAcademicYear);
  const [appliedAcademicYear, setAppliedAcademicYear] = useState<string | null>(
    null
  );

  const [editOpen, setEditOpen] = useState(false);
  const [editRows, setEditRows] = useState<YearRow[]>([]);
  const [editGrade, setEditGrade] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const { errors, setErrors, validate } = useAcademicYearValidation();
  const [hasSearched, setHasSearched] = useState(false);
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: SnackbarSeverityType;
  }>({ open: false, message: '', severity: SnackbarSeverityType.success });
  const [openAddDialog, setOpenAddDialog] = useState(false);

  const showToast = useCallback(
    (message: string, severity: SnackbarSeverityType) => {
      setToast({ open: true, message, severity });
    },
    []
  );
  const handleCloseToast = () => setToast({ ...toast, open: false });
  const effectiveAcademicYear = appliedAcademicYear || academicYear;

  const gradeOptions = useSchoolClassesConfig(loggedInUser?.school, showToast);
  const {
    entries: allYearEntries,
    isLoading,
    refresh
  } = useAcademicYears(loggedInUser?.school, showToast);

  const handleAcademicYearChange = (value: string) => {
    setAcademicYear(value);
    setAppliedAcademicYear(null);
    setHasSearched(false);
    setEditOpen(false);
    setOpenAddDialog(false);
    setErrors({});
  };

  const handleSearch = () => {
    setAppliedAcademicYear(academicYear);
    setHasSearched(true);
    refresh();
  };

  const academicYearRows = mapYearEntriesToGridRows(
    allYearEntries,
    appliedAcademicYear
  );

  const existingGradesForYear = academicYearRows.map((row) => row.grade);

  const academicYearColumns = getAcademicYearColumns(
    isLoading,
    (row: GradeRow) => openEdit(row)
  );

  const openEdit = (selectedRow: GradeRow) => {
    setEditGrade(selectedRow.grade);
    setEditRows([
      {
        id: effectiveAcademicYear,
        _id: selectedRow.entryId,
        startDate: selectedRow.startDate,
        endDate: selectedRow.endDate,
        grade: selectedRow.grade
      }
    ]);
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setErrors({});
  };

  const updateRow = (
    rowId: string,
    field: 'startDate' | 'endDate',
    newValue: string
  ) => {
    setEditRows((prevRows) =>
      prevRows.map((row) =>
        row.id === rowId ? { ...row, [field]: newValue } : row
      )
    );
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateEditFields = async (): Promise<boolean> => {
    const row = editRows[0];
    if (!row) return false;

    const isValid = await validate({
      startDate: row.startDate,
      endDate: row.endDate
    });

    return isValid;
  };

  const handleSave = async () => {
    if (!loggedInUser?.school || !editGrade) return;

    const isValid = await validateEditFields();
    if (!isValid) return;

    const payload: UpsertAcademicYearDatesBody = {
      grades: [editGrade],
      academicYearDates: {
        [effectiveAcademicYear]: {
          startDate: editRows[0].startDate,
          endDate: editRows[0].endDate
        }
      }
    };

    setIsSaving(true);
    try {
      await academicYearsApi.upsertAcademicYears(loggedInUser.school, payload);
      setEditOpen(false);
      await refresh();
      showToast('Academic year updated.', SnackbarSeverityType.success);
    } catch (e) {
      console.error(e);
      showToast('Failed to update academic year.', SnackbarSeverityType.error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AcademicYearContainer>
      <FormHeader variant='h4'>Academic Year Dates</FormHeader>
      <AcademicYearFilterCard
        academicYear={academicYear}
        onChange={handleAcademicYearChange}
        onSearch={handleSearch}
        isLoading={isLoading}
      />

      {hasSearched && (
        <AcademicYearTableCard
          rows={academicYearRows}
          columns={academicYearColumns}
          isLoading={isLoading}
          onAdd={() => setOpenAddDialog(true)}
        />
      )}

      <EditAcademicYearDialog
        open={editOpen}
        title={`Edit Academic Year - Grade ${editGrade} (${effectiveAcademicYear})`}
        rows={editRows}
        isSaving={isSaving}
        onClose={handleEditClose}
        onSave={handleSave}
        updateRow={updateRow}
        errors={errors}
      />

      <AddAcademicYearDialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        onSaved={refresh}
        gradeOptions={gradeOptions}
        existingGrades={existingGradesForYear}
        academicYear={effectiveAcademicYear as string}
        showToast={showToast}
      />

      <ToastMessage
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleCloseToast}
      />
    </AcademicYearContainer>
  );
};

export default AcademicYears;
